import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, ClipboardDocumentIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

const BackupCodesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState<string[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchBackupCodes();
  }, []);

  const fetchBackupCodes = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/two-factor/backup-codes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
      }
    } catch (error) {
      console.error('Failed to load backup codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm('Are you sure you want to regenerate backup codes? Your old codes will no longer work.')) {
      return;
    }
    setRegenerating(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/two-factor/backup-codes/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchBackupCodes();
      }
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy backup codes:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings/security')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Security Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Backup Codes</h1>
      <p className="mb-6 text-sm text-gray-600">
        Use these codes to access your account if you lose your authenticator device
      </p>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="w-5 h-5 text-indigo-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Your Backup Codes</h2>
          </div>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        </div>
        {codes.length === 0 ? (
          <div className="text-gray-500 text-sm">No backup codes available. Please enable 2FA first.</div>
        ) : (
          <>
            <div className="bg-gray-50 rounded-md p-4 mb-4">
              <ul className="grid grid-cols-2 gap-2">
                {codes.map((code, index) => (
                  <li key={index} className="font-mono text-sm text-gray-900">
                    {code}
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              {copied ? (
                <>
                  <CheckIcon className="w-5 h-5 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="w-5 h-5 mr-2" />
                  Copy All Codes
                </>
              )}
            </button>
          </>
        )}
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-900 mb-2">Important:</h4>
        <ul className="list-disc pl-5 text-sm text-yellow-700 space-y-1">
          <li>Each backup code can only be used once</li>
          <li>Store these codes in a safe place</li>
          <li>Regenerating codes will invalidate all previous codes</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupCodesPage;
